import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useNavigate } from 'react-router-dom';
import { Box, Text } from 'grommet';
import { FormPrevious } from 'grommet-icons';
import { useAppTheme } from '@Components/Context';
import {
  AppAnchor, AppButton, SubmissionLoader,
  Spinning,
} from '@Components/Control';
import { withProductAuth } from '@Components/Layout';
import { AuthNavWrapper } from '@Components/Navigation';
import { CidOnboardingTermsModal } from '@Components/Modal';
import {
  ProductFormSingleTextInput,
  ProductFormCheckBoxInput,
} from '@Components/Partial/Product/NewSearch';
import {
  initiateCreateCancellationRequest,
  initiateWithdrawCancellationRequest,
  fetchCancellationStatus,
} from '@Actions';
import { paths } from '@Components/configs';
import { SectionContainer } from './AccountPage';
import { StyledStatusBadge, StyledText } from './StyledAccountPage';

const formatDate = (date) => {
  const dateOptions = { month: 'long', day: '2-digit', year: 'numeric' };
  return new Date(date).toLocaleDateString('en-US', dateOptions);
};

const CidCancellationRequestPage = ({
  small,
  location,
  cancellationRequestedAt: cancellationRequestedAtRaw,
  effectiveCancellationDate: effectiveCancellationDateRaw,
  subscriptionLoading,
  submitLoading,
  createCancellationRequest,
  withdrawCancellationRequest,
  fetchCancellation,
}) => {
  const navigate = useNavigate();
  const { theme: { page, button }, isDarkMode } = useAppTheme();

  const [reason, setReason] = React.useState('');
  const [accepted, setAccepted] = React.useState(false);
  const [showTerms, setShowTerms] = React.useState(false);

  const cancellationRequestedAt = cancellationRequestedAtRaw
    ? new Date(cancellationRequestedAtRaw)
    : null;

  // Use the effective_cancellation_date from the backend (billing cycle end - 1 day).
  // Fall back to +30 days only when no request is active yet (pre-submission preview).
  const effectiveDate = effectiveCancellationDateRaw
    ? new Date(effectiveCancellationDateRaw)
    : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);

  const hasPendingRequest = !!cancellationRequestedAt;

  React.useEffect(() => {
    fetchCancellation();
  }, [fetchCancellation]);

  const handleSubmit = () => {
    createCancellationRequest(reason, () => {
      setReason('');
      setAccepted(false);
      fetchCancellation();
    });
  };

  const handleWithdraw = () => {
    withdrawCancellationRequest(() => navigate(paths.account));
  };

  const usePageBg = isDarkMode ? page.background : page.altBg;
  const useSectionBg = isDarkMode ? page.altBg : page.background;

  if (subscriptionLoading && !cancellationRequestedAtRaw) {
    return (
      <AuthNavWrapper
        small={small}
        location={location}
        bannerProps={{
          title: 'Account information',
          subTitle: 'Cancel subscription',
        }}
      >
        <Box
          flex
          align="center"
          justify="center"
          background={usePageBg}
          pad="2rem"
        >
          <Spinning size="large" color={page.highlight} />
        </Box>
      </AuthNavWrapper>
    );
  }

  return (
    <AuthNavWrapper
      small={small}
      location={location}
      bannerProps={{
        title: 'Account information',
        subTitle: hasPendingRequest
          ? 'Withdraw cancellation'
          : 'Cancel subscription',
      }}
    >
      <Box
        flex
        direction="column"
        background={usePageBg}
        pad={{ vertical: '2rem', horizontal: small ? '1rem' : '1.5rem' }}
      >
        {submitLoading && (
          <SubmissionLoader
            textColor={page.primaryText}
            loaderColor={page.highlight}
            message="Processing request..."
            contentWidth="18rem"
          />
        )}

        <Box pad={{ bottom: '2rem' }}>
          <AppAnchor path={paths.account}>
            <Box direction="row" align="center" gap="0.15rem">
              <FormPrevious size="0.85rem" color={page.altHighlight} />
              <Text size="0.85rem" color={page.altHighlight}>Back to account</Text>
            </Box>
          </AppAnchor>
        </Box>

        <SectionContainer
          small={small}
          sectionTitle={hasPendingRequest ? 'Withdraw cancellation' : 'Cancel subscription'}
          customWidth={small ? '100%' : '40%'}
          textColor={page.primaryText}
          sectionBg={useSectionBg}
        >
          {hasPendingRequest ? (
            <Box direction="column" gap="1.25rem">
              <StyledStatusBadge background="#FCF8EF">
                <Text size="1rem" color="#AB5741" weight={600}>
                  Cancellation pending — effective
                  {' '}
                  {formatDate(effectiveDate)}
                </Text>
              </StyledStatusBadge>
              <StyledText size="1rem" color={page.primaryText}>
                Your cancellation request was submitted on
                {' '}
                <strong>{formatDate(cancellationRequestedAt)}</strong>
                . Billing will stop at the end of your current billing cycle, after which only
                {' '}
                <strong>Content ID access</strong>
                {' '}
                will remain until
                {' '}
                <strong>{formatDate(effectiveDate)}</strong>
                , when your subscription fully ends.
              </StyledText>
              <StyledText size="1rem" color={page.primaryText}>
                Changed your mind? You can withdraw this request any time before the effective date.
              </StyledText>
              <Box direction="row" gap="1rem" align="center" pad={{ bottom: '1rem' }}>
                <Box width="fit-content">
                  <ProductFormCheckBoxInput
                    small={small}
                    key="withdraw-checkbox"
                    bgColor="white"
                    borderColor="var(--nav-border)"
                    checkmarkColor={page.primaryText}
                    name="withdraw-checkbox"
                    focusHighlight={page.focusHighlight}
                    value={accepted}
                    handleFormValues={(val) => setAccepted(val)}
                  />
                </Box>
                <Text size="1rem" color={page.primaryText}>
                  I want to withdraw my cancellation request
                </Text>
              </Box>
              <AppButton
                overrideHover
                onClick={handleWithdraw}
                level="authSubmit"
                disabled={!accepted || submitLoading}
                bgColor={button.primaryBg}
                color="white"
                label="Withdraw cancellation request"
                alignSelf="start"
              />
            </Box>
          ) : (
            <Box direction="column" gap="1.25rem">
              <StyledText size="1rem" color={page.primaryText}>
                We&#39;re sorry to see you go. As per our
                {' '}
                <Text
                  color={page.highlight}
                  style={{ textDecoration: 'underline', cursor: 'pointer' }}
                  onClick={() => setShowTerms(true)}
                >
                  Terms
                </Text>
                , subscription cancellations require a
                {' '}
                <strong>30-day notice period</strong>
                .
              </StyledText>
              <Box
                direction="column"
                gap="0.5rem"
                pad={{ vertical: '0.5rem', horizontal: '1rem' }}
                background={isDarkMode ? page.altBg : '#F9FAFB'}
                round="small"
              >
                <Box direction="row" gap="0.5rem" align="center" wrap>
                  <Text size="0.9rem" color={page.secondaryText}>Request date</Text>
                  <Text size="0.9rem" color={page.primaryText} weight={500}>
                    {formatDate(new Date())}
                  </Text>
                </Box>
                <Box direction="row" gap="0.5rem" align="center" wrap>
                  <Text size="0.9rem" color={page.secondaryText}>Effective cancellation date</Text>
                  <Text size="0.9rem" color={page.primaryText} weight={500}>
                    {formatDate(effectiveDate)}
                  </Text>
                </Box>
              </Box>
              <StyledText size="1rem" color={page.primaryText}>
                Billing will stop at the end of your current billing cycle. After that, your
                access will be limited to
                {' '}
                <strong>Content ID features only</strong>
                {' '}
                until
                {' '}
                <strong>{formatDate(effectiveDate)}</strong>
                , when your subscription fully ends.
              </StyledText>
              <ProductFormSingleTextInput
                placeholder="Reason for cancellation (optional)"
                value={reason}
                focusHighlight={page.focusHighlight}
                handleFormValues={(val) => setReason(val)}
              />
              <Box direction="row" gap="1rem" align="center" pad={{ bottom: '1rem' }}>
                <Box width="fit-content">
                  <ProductFormCheckBoxInput
                    small={small}
                    key="accept-checkbox"
                    bgColor="white"
                    borderColor="var(--nav-border)"
                    checkmarkColor={page.primaryText}
                    name="accept-checkbox"
                    focusHighlight={page.focusHighlight}
                    value={accepted}
                    handleFormValues={(val) => setAccepted(val)}
                  />
                </Box>
                <Text size="1rem" color={page.primaryText}>
                  I understand billing stops at my billing cycle end, and my subscription
                  {' '}
                  fully ends on
                  {' '}
                  <strong>{formatDate(effectiveDate)}</strong>
                </Text>
              </Box>
              <AppButton
                overrideHover
                onClick={handleSubmit}
                level="authSubmit"
                disabled={!accepted || submitLoading}
                bgColor="var(--app-error)"
                color="white"
                label="Submit cancellation request"
                alignSelf="start"
              />
            </Box>
          )}
        </SectionContainer>
      </Box>
      {showTerms && (
        <CidOnboardingTermsModal
          toggle={() => setShowTerms(false)}
          isSmall={small}
          currentYear={new Date().getFullYear()}
          background={page.modalBg}
          textColor={page.modalText}
        />
      )}
    </AuthNavWrapper>
  );
};

function mapStateToProps(state) {
  return {
    cancellationRequestedAt: state.payment.cancellationRequestedAt,
    effectiveCancellationDate: state.payment.effectiveCancellationDate,
    submitLoading: state.fetchLoader.submissionLoading,
    subscriptionLoading: state.fetchLoader.dataLoading,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators({
    createCancellationRequest: initiateCreateCancellationRequest,
    withdrawCancellationRequest: initiateWithdrawCancellationRequest,
    fetchCancellation: fetchCancellationStatus,
  }, dispatch);
}

CidCancellationRequestPage.propTypes = {
  small: PropTypes.bool.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
  }).isRequired,
  cancellationRequestedAt: PropTypes.string,
  effectiveCancellationDate: PropTypes.string,
  subscriptionLoading: PropTypes.bool.isRequired,
  submitLoading: PropTypes.bool.isRequired,
  createCancellationRequest: PropTypes.func.isRequired,
  withdrawCancellationRequest: PropTypes.func.isRequired,
  fetchCancellation: PropTypes.func.isRequired,
};

CidCancellationRequestPage.defaultProps = {
  cancellationRequestedAt: null,
  effectiveCancellationDate: null,
};

export default connect(mapStateToProps, mapDispatchToProps)(
  withProductAuth(CidCancellationRequestPage),
);
